import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useIntl } from 'umi';
import { SegmentMultiple } from 'tea-component';
import { translateSpecialWord } from "@/pages/datalab/utils/utils";
import SvgIcon from '@home/components/SvgIcon';
import useScrollbarDetection from '@/pages/datalab/utils/hooks/useScrollbarDetection';
import { useUrlParam } from "@dbl/core/global";
import type {
  IChartTagCategoryItemDto,
  IGetChartLabelsSubLabelDto,
  IGetChartListSubLabelRequest,
} from '@dbl/services/chart';

import './index.scss';

// Category ref 类型
export interface CategoryRef {
  resetState: () => void;
}

type TCateProps<
  S = string,
  A = Array<unknown>,
  B extends boolean = boolean
> = {
  labelsHeadText: S;
  subLabelsHeadText: S;
  labels: A;
  subLabels: A;
  defaultOptions: A;
  isEn: B;
  isShowLabels: B;
  isShowExpand?: B;
};

const Category = React.forwardRef<CategoryRef, Partial<TCateProps>>((props, ref) => {
  const { labelsHeadText = '', subLabelsHeadText = '', labels = [], subLabels = [], isEn, isShowLabels = true, isShowExpand = false } = props; // labels是一级类目原始数据, subLabels是二级类目原始数据
  const { formatMessage } = useIntl();
  const { scrollInfo, waitForRenderAndCheck } = useScrollbarDetection({ element: document.querySelector('.dbl-category-content-flex-hide') });
  const [oneValue, setOneValue] = useState(['all']); // 一级类目选中的值
  const [oneOptions, setOneOptions] = useState([]); // 一级类目页面显示选项
  const [twoValue, setTwoValue] = useUrlParam({
    urlKey: 'twoValue',
    defaultValue: ['all'],
  }); // 上传sub_labels; // 二级类目选中的值
  const [twoOptions, setTwoOptions] = useState([]); // 二级类目页面显示选项
  const [isExpanded, setIsExpanded] = useUrlParam({
    urlKey: 'isExpanded',
    defaultValue: false,
  });

  // 使用 useRef 管理组件内部状态，避免模块级变量导致的状态污染
  const subLabelObjRef = useRef<Record<string, IChartTagCategoryItemDto[]>>({});
  const allParentNamesRef = useRef<string[]>([]); // 存储所有一级类目的name, 当用户选中[全部]选项时, 可以直接使用这个数组, 无需重新遍历
  const subLabelNamesRef = useRef<string[]>([]); // 存储所有二级类目的name, 当用户选中[全部]选项时, 可以直接使用这个数组, 无需重新遍历


  useEffect(() => {
    if(!isShowExpand) return;
    // 组件挂载后开始检测
    waitForRenderAndCheck();
  }, [isShowExpand, waitForRenderAndCheck]);

  const hasVerticalScrollbar = useMemo(() => {
    if(!isShowExpand) return false;
    if (isExpanded) return true;
    return scrollInfo?.hasVerticalScrollbar || scrollInfo?.scrollHeight > 44;
  }, [isExpanded, isShowExpand, scrollInfo?.hasVerticalScrollbar, scrollInfo?.scrollHeight]);

  const handleClick = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    // 先转换subLabels的结构
    subLabelObjRef.current = transformSubLabelsStruct(labels, subLabels);
    const first = getOptions(labels, isEn);

    const second = getOptions(Object.values(subLabelObjRef.current).flat(), isEn);
    // 设置 一级类目 选项
    setOneOptions(first.options);
    // 设置 二级类目 选项
    setTwoOptions(second.options);
    // 存储[全部]选项对应的上传项
    allParentNamesRef.current = first.keys;
    subLabelNamesRef.current = second.keys;
    // 进入页面时, 默认高亮[全部]选项, 所以需要上传所有的二级类目.
    // props?.onChange(getRequestParam(oneValue, twoValue), true);
  }, [props?.labels, props?.subLabels, isEn]);

  useImperativeHandle(ref, () => ({
    resetState: () => {
      setOneValue(['all']);
      setTwoValue(['all']);
    },
  }));

  // 选项项类型定义
  interface IOptionItem {
    value: string;
    text: string;
    count: number;
  }

  /**
   * 获取 页面要显示的选项options
   * @param data 传入的labels数据
   * @return {options, keys} options 用于 SegmentMultiple的options属性
   *                         keys 是一级类目所有的id, 或者二级类目所有的name, 在首次进入页面的时候, 会存储起来, 在点击[全部]选                              项时使用.
   */
  const getOptions = (data: Array<{ name: string; count?: number }> = [], isEn?: boolean) => {
    const map = new Map<string, IOptionItem>(); // 利用Map唯一性, 可以对二级类目的名称进行去重操作, 同时保证了一级和二级类目的显示顺序与后端返回顺序保持一致.
    let total = 0;
    data.forEach((segment) => {
      if (!segment) return;
      const segmentCount = segment.count || 0;
      if (map.has(segment.name)) {
        // 如果是二级类目, 页面显示时name要去重, count相加
        total += segmentCount;
        const count = map.get(segment.name)!.count + segmentCount;
        map.set(segment.name, {
          value: segment.name,
          text: `${translateSpecialWord(segment.name, isEn)}(${count})`,
          count,
        });
      } else {
        map.set(segment.name, {
          value: segment.name,
          text: `${translateSpecialWord(segment.name, isEn)}(${segmentCount})`,
          count: segmentCount,
        });
        total += segmentCount;
      }
    });
    
    const options = [...map.values()].sort((a, b) => {
      return b.count - a.count;
    });
    options.unshift({value: 'all', text: `${isEn ? 'All' : '全部'}(${total})`});
    return { options, keys: [...map.keys()] }; // 利用扩展运算符, 将Map entries类型隐性转换成 Array类型.
  };

  // 将后端返回的sub_labels转换为对象形式, 便于在切换一级类目时, 快速获取二级类目数据
  const transformSubLabelsStruct = (
    labels: IChartTagCategoryItemDto[] = [],
    subLabels: IGetChartLabelsSubLabelDto[] = [],
  ): Record<string, IChartTagCategoryItemDto[]> => {
    const subObj = subLabels?.reduce(
      (acc: Record<string, IChartTagCategoryItemDto[] | undefined>, subLabel) => {
        // acc用于保存sub_labels的对象形式
        acc[subLabel?.parent_label_name] = subLabel?.details;
        return acc;
      },
      {},
    );
     // 后端将一级类目排序后返回前端, 前端根据一级类目的顺序, 排序二级类目
    return labels?.reduce((acc: Record<string, IChartTagCategoryItemDto[]>, label) => {
      // 当后端返回了一级类目, 但是没有返回一级类目对应的二级类目的情况下
      if (subObj[label?.name]) acc[label?.name] = subObj[label?.name] || [];
      return acc;
    }, {});
  };

  /**
   * 上传数据格式如下:
   * sub_labels:[
   *         {
   *             "parent_label_name":"个人所见",
   *             "name":"sublabel名称",
   *         },
   * @param parentNames 所有被选中的一级类目id
   * @param subNames 所有被选中的二级类目name
   */
  const getRequestParam = (parentNames: string[] = [], subNames: string[] = []) => {
    // 当一级类目和二级类目都是 [全部]选项高亮返回
    if (parentNames[0] === 'all' && subNames[0] === 'all') {
      return { subLabels: [], allSelected: true };
    }

    if (parentNames[0] === 'all') parentNames = allParentNamesRef.current;
    if (subNames[0] === 'all') subNames = subLabelNamesRef.current;
    
    const subLabels: IGetChartListSubLabelRequest[] = [];
    parentNames.forEach((parentName) => {
      subNames.forEach((name) => {
        subLabels.push({ parent_label_name: parentName, name });
      });
    });
    return { subLabels, allSelected: false };
  };

  // 判断 全部 和 其他类目
  const handleValues = (value: string[] = [], previous: string[] = []) => {
    if (previous.length === 1 && previous[0] === 'all' && value.length === 2) {
      // eslint-disable-next-line no-param-reassign
      value = value.filter((item) => item !== 'all');
    } else if (previous.some((item) => item === 'all') === false && value.some((item) => item === 'all')) {
      // eslint-disable-next-line no-param-reassign
      value = ['all'];
    }
    return value;
  };

  return (
    <div>
      {
        isShowLabels && <div className="add-steep-end">
          <div className="add-steep-flex">
            <div className="add-steep-laber">{labelsHeadText}</div>
            <SegmentMultiple
              options={oneOptions}
              value={oneValue}
              onChange={(newValue) => {
                newValue = handleValues(newValue, oneValue);
                setOneValue(newValue);
                props?.onChange(getRequestParam(newValue, twoValue));

                // 重新计算显示的二级类目的options
                let parentNames = newValue;
                if (parentNames[0] === 'all') parentNames = allParentNamesRef.current;
                const subLabels = parentNames.map((name) => subLabelObjRef.current[name]);
                const { options } = getOptions(subLabels?.flat(), isEn);
                setTwoOptions(options);
              }}
            />
          </div>
          {props.children}
        </div>
      }
      {
        isShowExpand ? <div className={`dbl-category-content ${hasVerticalScrollbar ? '' : 'dbl-category-content-hide'}`}>
          <div className="dbl-category-content-label">{subLabelsHeadText}</div>
          <div className="dbl-category-content-flex">
            <SegmentMultiple
              className={`dbl-category-content-flex-${isExpanded ? 'show' : 'hide'}`}
              options={twoOptions}
              value={twoValue}
              onChange={(newValue) => {
                newValue = handleValues(newValue, twoValue);
                setTwoValue(newValue);
                props?.onChange(getRequestParam(oneValue, newValue));
              }}
            />
            {isExpanded ? <>
              <div className="dbl-category-content-flex-btn"></div>
              <div
                className="dbl-category-content-flex-btn dbl-category-btn-collapse"
                onClick={handleClick}>
                {formatMessage({id: 'dbl_common_collapse'})}
                <SvgIcon type="point-down" className="dbl-category-content-flex-btn-collapse" />
              </div>
            </> : <div
              className="dbl-category-content-flex-btn"
              onClick={handleClick}>
              {formatMessage({id: 'dbl_common_expand'})}
              <SvgIcon type="point-down" className="dbl-category-content-flex-btn-expand" />
            </div>}
          </div>
        </div> : <div className="add-steep-flex">
          <div className="add-steep-laber">{subLabelsHeadText}</div>
          <SegmentMultiple
            options={twoOptions}
            value={twoValue}
            onChange={(newValue) => {
              newValue = handleValues(newValue, twoValue);
              setTwoValue(newValue);
              props?.onChange(getRequestParam(oneValue, newValue));
            }}
          />
        </div>
      }
    </div>
  );
});

Category.displayName = 'Category';

export default Category;
